/**
 * Red de seguridad de la app.
 *
 * Hasta ahora no había ninguna: cualquier error lanzado durante el render
 * desmontaba el árbol completo de React y dejaba la pantalla en blanco, sin
 * mensaje, sin forma de recuperarse y sin dejar rastro. El síntoma clásico era
 * entrar a una ruta y ver blanco, recargar, y que funcionara.
 *
 * La causa más frecuente de eso en una SPA con code splitting es el **chunk
 * vencido**: el navegador tiene el index.html de un deploy anterior, que apunta
 * a archivos con un hash que ya no existe en el CDN. El import dinámico falla,
 * React lanza, y sin boundary la pantalla queda vacía. Ese caso se resuelve solo
 * recargando una vez, y eso es lo que hace `lazyWithRetry`.
 *
 * Para todo lo demás, este boundary muestra algo entendible y deja el error en
 * la consola y en analytics, en vez de perderlo.
 */
import { Component, type ReactNode } from 'react';
import { track } from '@/lib/analytics';
import { esErrorDeChunk } from '@/lib/app-version';

interface Props { children: ReactNode }
interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    try {
      track('app_error', {
        message: error.message,
        name: error.name,
        path: window.location.pathname,
      });
    } catch {
      // Si analytics falla, no puede tumbar la pantalla de error.
    }
  }

  override render() {
    if (!this.state.error) return this.props.children;

    // Un fallo de carga de módulo (deploy vencido que declinó recargar, o un
    // problema de red) no es "algo se rompió": es "esta parte no llegó".
    // El tono cambia — a la comunidad no se le dice que el club está roto
    // cuando lo único que pasó es que hay una versión nueva o falló la red.
    const esCarga = esErrorDeChunk(this.state.error);

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#0B1A1F] px-6 text-center text-white">
        <h1 className="font-display text-2xl font-bold">
          {esCarga ? 'Un segundo — estamos poniéndonos al día' : 'Esto no salió como esperábamos'}
        </h1>
        <p className="mt-2 max-w-sm text-sm leading-relaxed text-white/60">
          {esCarga
            ? 'Publicamos mejoras o tu conexión parpadeó. Recarga y sigues donde estabas.'
            : 'No es culpa tuya. Recarga la página y deberías poder seguir donde estabas.'}
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-full bg-brand-energy px-6 py-3 font-semibold text-[#071114] transition-opacity hover:opacity-90"
          >
            Recargar
          </button>
          <a
            href="/"
            className="rounded-full border border-white/20 px-6 py-3 text-sm text-white/80 transition-colors hover:bg-white/5"
          >
            Ir al inicio
          </a>
        </div>
        <p className="mt-8 text-xs text-white/30">
          Si vuelve a pasar, escríbenos a soporte@gurumba.com
        </p>
      </div>
    );
  }
}
